/**
 * useAppState Composable
 *
 * Centralized access to application-level state from Pinia useAppStore.
 * Provides reactive computed refs and actions for app-wide concerns.
 *
 * @version 2.0.0
 * @migrated-from Vuex useStore() → Pinia useAppStore()
 */

import { computed, ref, onMounted, type ComputedRef, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores/app'

// =============================================================================
// Types
// =============================================================================

export interface UseAppStateReturn {
  // State from store (storeToRefs → Ref)
  readonly appTitle: Ref<string>
  readonly appShortTitle: Ref<string>
  readonly appVersion: Ref<string>
  readonly networkOnLine: Ref<boolean>
  readonly splashShown: Ref<boolean>
  readonly refreshingApp: Ref<boolean>
  readonly isPageFullScreen: Ref<boolean>
  readonly loading: Ref<boolean>
  readonly snackbarMessage: Ref<string>
  readonly showSnackbarSuccessMessage: Ref<boolean>
  readonly showSnackbarErrorMessage: Ref<boolean>
  readonly showSnackbarInfoMessage: Ref<boolean>
  // Derived computed
  readonly newContentAvailable: ComputedRef<boolean>
  readonly isMobile: ComputedRef<boolean>

  // Actions
  setSplashShown: () => void
  setLoading: (value: boolean) => void
  serviceWorkerSkipWaiting: () => Promise<void>
  dismissServiceWorkerUpdate: () => void
  resetSnackbarSuccess: () => void
  resetSnackbarError: () => void
  resetSnackbarInfo: () => void
}

// =============================================================================
// Mobile Detection Regex
// =============================================================================

const MOBILE_REGEX = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i

// =============================================================================
// Composable
// =============================================================================

/**
 * Composable for accessing application-level state.
 * Thin wrapper around Pinia useAppStore for backward compatibility.
 *
 * @example
 * ```ts
 * const { appVersion, networkOnLine, loading, isMobile, setLoading } = useAppState()
 * ```
 */
export function useAppState(): UseAppStateReturn {
  const appStore = useAppStore()
  const {
    appTitle,
    appShortTitle,
    appVersion,
    networkOnLine,
    splashShown,
    refreshingApp,
    isPageFullScreen,
    loading,
    snackbarMessage,
    showSnackbarSuccessMessage,
    showSnackbarErrorMessage,
    showSnackbarInfoMessage,
  } = storeToRefs(appStore)

  // Local state for mobile detection
  const isMobileDevice = ref(false)

  const newContentAvailable = computed(() => appStore.newContentAvailable)
  const isMobile = computed(() => isMobileDevice.value)

  // =============================================================================
  // Actions (delegate to Pinia store)
  // =============================================================================

  function setSplashShown(): void {
    appStore.setSplashShown(true)
  }

  function setLoading(value: boolean): void {
    appStore.setLoadingState(value)
  }

  async function serviceWorkerSkipWaiting(): Promise<void> {
    await appStore.serviceWorkerSkipWaiting()
  }

  function dismissServiceWorkerUpdate(): void {
    appStore.dismissServiceWorkerUpdate()
  }

  function resetSnackbarSuccess(): void {
    appStore.showSnackbarSuccessMessage = false
  }

  function resetSnackbarError(): void {
    appStore.showSnackbarErrorMessage = false
  }

  function resetSnackbarInfo(): void {
    appStore.showSnackbarInfoMessage = false
  }

  // =============================================================================
  // Lifecycle
  // =============================================================================

  onMounted(() => {
    isMobileDevice.value = MOBILE_REGEX.test(navigator.userAgent)
  })

  // =============================================================================
  // Return
  // =============================================================================

  return {
    appTitle,
    appShortTitle,
    appVersion,
    networkOnLine,
    splashShown,
    refreshingApp,
    isPageFullScreen,
    loading,
    snackbarMessage,
    showSnackbarSuccessMessage,
    showSnackbarErrorMessage,
    showSnackbarInfoMessage,
    newContentAvailable,
    isMobile,

    setSplashShown,
    setLoading,
    serviceWorkerSkipWaiting,
    dismissServiceWorkerUpdate,
    resetSnackbarSuccess,
    resetSnackbarError,
    resetSnackbarInfo,
  }
}

export default useAppState
